import { useEffect, useMemo, useState } from 'react'
import { Button } from '../ui'
import { ApiError } from '../../api/client'
import {
  SPLIT_DEFAULT_SEPARATORS,
  SPLIT_LOCATION_TYPES,
  splitOnSegments,
  useMappingSplitOn,
} from '../../api/hooks'
import type { MappingBulkFailure } from '../../api/hooks'
import type { AccessSystem, MappingItem } from '../../api/types'
import { DecisionRunModal } from './DecisionRunModal'

/** One node of the merged preview tree (shared prefixes across the selected
 *  rows collapse into one node, exactly like the find-or-create apply does). */
interface PreviewNode {
  name: string
  children: PreviewNode[]
}

function buildTree(paths: string[][]): PreviewNode[] {
  const roots: PreviewNode[] = []
  for (const path of paths) {
    let level = roots
    for (const name of path) {
      let node = level.find((n) => n.name === name)
      if (!node) {
        node = { name, children: [] }
        level.push(node)
      }
      level = node.children
    }
  }
  return roots
}

function PreviewTree({ nodes, depth }: { nodes: PreviewNode[]; depth: number }) {
  return (
    <ul className={depth > 0 ? 'ml-4 border-l border-ostrel-line pl-2' : ''}>
      {nodes.map((node) => (
        <li key={node.name} className="tnum text-[11px] text-ostrel-ink">
          <span className="text-ostrel-ink-muted">{'└ '}</span>
          <span>{node.name}</span>
          {node.children.length > 0 && <PreviewTree nodes={node.children} depth={depth + 1} />}
        </li>
      ))}
    </ul>
  )
}

/** Default location type per level: level 1 is the address, the leaf the
 *  apartment, anything between a floor. */
function defaultLevelTypes(depth: number): string[] {
  return Array.from({ length: depth }, (_, i) =>
    i === 0 ? 'address' : i === depth - 1 ? 'apartment' : 'floor',
  )
}

/** Split-on tree import v2 for the Groups step of the mapping wizard: vendor
 *  folder names like `Armborstvägen 12 – Lgh 1001` carry a location path in
 *  the name. The name splits on ANY of `–`, `-`, `/` (plus one optional
 *  custom separator). NOTHING saves from the preview: the operator first
 *  renders the real tree — merged across the selected rows, one location-type
 *  select per level (level 1 defaults address, leaf apartment; rows without a
 *  separator are kept as-is, top-level) — then Apply (disabled until the preview is
 *  shown) sends the whole selection as ONE split-on call carrying the paths
 *  exactly as previewed; the server finds-or-creates the location + group
 *  chains (the group chain nests, so `Lgh 1001` under two addresses never
 *  collides) and attaches each folder to its leaf group. Per-row failures
 *  are collected and surfaced; failed rows stay selected for retry. */
export function SplitOnTool({
  system,
  items,
  site,
  onApplied,
  onBusyChange,
}: {
  system: AccessSystem
  /** The selected undecided group candidates (wizard checkbox selection). */
  items: MappingItem[]
  /** The workbench's selected site — the split-on mutation patches the
   *  site-scoped board cache. */
  site?: string
  /** Called with the per-row failures once the batch settles, so the
   *  wizard can keep exactly those rows selected for retry. */
  onApplied?: (failures: MappingBulkFailure[]) => void
  /** Reports whether a run/modal is in flight so the wizard pins this step
   *  (an apply that empties the step must not unmount the run log). */
  onBusyChange?: (busy: boolean) => void
}) {
  const split = useMappingSplitOn(system.unique_id, site)
  const [customSeparator, setCustomSeparator] = useState('')
  const [previewed, setPreviewed] = useState(false)
  const [levelTypes, setLevelTypes] = useState<string[]>([])
  const [applyOpen, setApplyOpen] = useState(false)
  const [log, setLog] = useState<string[]>([])
  const [failures, setFailures] = useState<MappingBulkFailure[]>([])

  const busy = applyOpen || split.isPending
  useEffect(() => {
    onBusyChange?.(busy)
  }, [busy, onBusyChange])

  // Any change to the inputs invalidates the preview — Apply must always act
  // on what the operator is looking at. (itemsKey: the selection array gets
  // a fresh identity every render, so key the effect off its contents.)
  const itemsKey = items.map((i) => i.vendor_uid).join(' ')
  useEffect(() => {
    setPreviewed(false)
  }, [customSeparator, itemsKey])

  const parsed = useMemo(
    () =>
      items.map((item) => ({
        item,
        segments: splitOnSegments(item.display, customSeparator),
      })),
    [items, customSeparator],
  )
  const splittable = parsed.filter((p) => p.segments.length >= 2)
  const unsplittable = parsed.filter((p) => p.segments.length < 2)
  const maxDepth = splittable.reduce((max, p) => Math.max(max, p.segments.length), 0)
  const tree = useMemo(
    () => buildTree(splittable.map((p) => p.segments)),
    [splittable],
  )

  const preview = () => {
    setLevelTypes(defaultLevelTypes(maxDepth))
    setFailures([])
    setPreviewed(true)
  }

  /** One log line per applied chunk plus one per failed row — the modal
   *  streams them as the chunks land. The row count freezes at run start:
   *  the selection clears as rows bind, and the dialog must not retitle
   *  itself to '0 rows' mid-flow. */
  const [runTotal, setRunTotal] = useState(0)
  const apply = () => {
    if (split.isPending || parsed.length === 0) return
    const total = parsed.length
    setLog([])
    setRunTotal(total)
    let prevDone = 0
    split.mutate(
      {
        items: parsed.map((p) => p.item),
        customSeparator,
        levelTypes,
        // Managed only on a writable system — a dry-run system 400s a managed
        // decide (WritesNotAllowed).
        manage: !system.dry_run,
        onProgress: (done, _total, chunkFailures) => {
          const lines = [`✓ applied ${done - prevDone} (${done}/${total})`]
          prevDone = done
          for (const f of chunkFailures) lines.push(`✗ ${f.display} — ${f.detail}`)
          setLog((prev) => [...prev, ...lines])
        },
      },
      {
        onSuccess: (f) => {
          setLog((prev) => [
            ...prev,
            f.length === 0
              ? `done — all ${total} rows applied`
              : `done — ${total - f.length} applied, ${f.length} failed (left selected for retry)`,
          ])
          setFailures(f)
          onApplied?.(f)
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3.5 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Split on {SPLIT_DEFAULT_SEPARATORS.join(' ')}
        </span>
        <input
          type="text"
          value={customSeparator}
          onChange={(e) => setCustomSeparator(e.target.value)}
          aria-label="Custom separator"
          placeholder="custom"
          className="tnum w-20 rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1 text-center text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
        />
        <span className="tnum text-[11px] text-ostrel-ink-muted">
          {items.length === 0
            ? 'Select rows above to preview the split.'
            : `${items.length} selected — each becomes a location tree + a leaf group.`}
        </span>
        <span className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={items.length === 0 || split.isPending}
            onClick={preview}
          >
            Preview tree
          </Button>
          <Button
            variant="navy"
            size="sm"
            disabled={!previewed || parsed.length === 0 || split.isPending}
            onClick={() => setApplyOpen(true)}
          >
            Apply split for {parsed.length} rows
          </Button>
        </span>
      </div>
      <DecisionRunModal
        open={applyOpen}
        title={`Apply split for ${log.length > 0 || split.isPending ? runTotal : parsed.length} rows`}
        confirmLabel="Apply split"
        confirmText={
          <p>
            Build the previewed location + group trees and attach {parsed.length}{' '}
            {parsed.length === 1 ? 'folder' : 'folders'} to their leaf groups. Runs in chunks — the
            log streams as they land.
          </p>
        }
        log={log}
        running={split.isPending}
        error={split.error ? (split.error instanceof ApiError ? split.error.detail : split.error.message) : null}
        onConfirm={apply}
        onClose={() => setApplyOpen(false)}
      />
      {previewed && (
        <div
          aria-label="Split preview"
          className="flex flex-col gap-2 rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2"
        >
          {maxDepth > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {Array.from({ length: maxDepth }, (_, i) => (
                <label key={i} className="flex items-center gap-1.5 text-[11px] text-ostrel-ink-muted">
                  Level {i + 1}
                  {i === maxDepth - 1 && maxDepth > 1 ? ' (leaf)' : ''} type
                  <select
                    aria-label={`Level ${i + 1} type`}
                    value={levelTypes[i] ?? 'floor'}
                    onChange={(e) =>
                      setLevelTypes((prev) => prev.map((t, j) => (j === i ? e.target.value : t)))
                    }
                    className="rounded-sm border border-ostrel-line bg-ostrel-card px-1.5 py-0.5 text-[11px] text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                  >
                    {SPLIT_LOCATION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          )}
          {tree.length > 0 ? (
            <div className="max-h-48 overflow-y-auto">
              <PreviewTree nodes={tree} depth={0} />
            </div>
          ) : (
            unsplittable.length === 0 && (
              <p className="text-[11px] text-ostrel-ink-muted">
                Nothing to split — no selected name carries a separator.
              </p>
            )
          )}
          {unsplittable.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {unsplittable.map((p) => (
                <li key={p.item.vendor_uid} className="tnum text-[11px] text-ostrel-ink">
                  <span className="font-medium">{p.item.display}</span>
                  <span className="text-ostrel-ink-muted"> — no separator, kept as-is (top level)</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-ostrel-ink-muted">
            Preview only — nothing is saved until Apply.
          </p>
        </div>
      )}
      {split.error && (
        <p className="text-[11px] text-ostrel-danger">
          {split.error instanceof ApiError ? split.error.detail : split.error.message}
        </p>
      )}
      {failures.length > 0 && (
        <div className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-danger">
            {failures.length} failed to split
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {failures.map((f) => (
              <li key={f.vendor_uid} className="text-[11px] text-ostrel-danger">
                {f.display} — {f.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
